/**
 * Weekly Off Calendar — separate from the Holiday calendar.
 *
 * Policy days (Sun / Sat rule) come pre-filled. Click any other date to add a weekly off,
 * click a policy day to turn it into a WORKING day for this month only (e.g. an exam Sunday).
 */
import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { CalendarOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE as API_URL } from '../lib/apiBase';
import { safeJson } from '../lib/safeJson';

const DOW = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const headers = () => {
  const t = localStorage.getItem('hrms-token');
  return t ? { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
};

export function WeeklyOffCalendar({ entityId, month, data, onChange, disabled }) {
  const [busy, setBusy] = useState(false);

  const weekOff = useMemo(() => new Set(data?.week_off_dates || []), [data]);
  const policy = useMemo(() => new Set(data?.policy_week_off_dates || []), [data]);
  const marked = useMemo(() => new Set(data?.marked_week_off_dates || []), [data]);
  const working = useMemo(() => new Set(data?.working_day_dates || []), [data]);
  const holidays = useMemo(() => new Set(data?.holiday_dates || []), [data]);

  const cells = useMemo(() => {
    if (!month) return [];
    const [y, m] = month.split('-').map(Number);
    const first = new Date(y, m - 1, 1).getDay();
    const last = new Date(y, m, 0).getDate();
    const out = Array.from({ length: first }, () => null);
    for (let d = 1; d <= last; d++) {
      out.push({ day: d, iso: `${month}-${String(d).padStart(2, '0')}` });
    }
    return out;
  }, [month]);

  const toggle = async (iso) => {
    if (disabled || busy) return;
    if (holidays.has(iso)) {
      toast.error('This date is a declared holiday. Remove it from the Holiday calendar first.');
      return;
    }
    const action = weekOff.has(iso) ? 'working_day' : 'weekly_off';
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/attendance/monthly-approval/${entityId}/${month}/day-action`, {
        method: 'POST', headers: headers(), credentials: 'include',
        body: JSON.stringify({ action, dates: [iso], label: action === 'weekly_off' ? 'Weekly Off' : 'Working Day' }),
      });
      const d = await safeJson(res);
      if (!res.ok) throw new Error(d.detail || 'Could not update the weekly off');
      toast.success(action === 'weekly_off'
        ? `${iso} is now a paid weekly off`
        : `${iso} is now a working day`);
      onChange?.();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="weekly-off-calendar">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <CalendarOff className="w-4 h-4 text-blue-600" />
          Weekly Off Calendar
          <Badge variant="outline" className="text-[10px] font-normal">{weekOff.size} off day(s)</Badge>
          {busy && <Loader2 className="w-3 h-3 animate-spin text-slate-400" />}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-7 gap-1 mb-1">
          {DOW.map((d, i) => (
            <div key={i} className="text-center text-[10px] font-semibold text-slate-400">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((c, i) => {
            if (!c) return <div key={`pad-${i}`} className="h-9" />;
            const isOff = weekOff.has(c.iso);
            const isHoliday = holidays.has(c.iso);
            const isException = working.has(c.iso);
            const fromPolicy = policy.has(c.iso) && !marked.has(c.iso);
            return (
              <button
                key={c.iso}
                type="button"
                disabled={disabled || busy}
                onClick={() => toggle(c.iso)}
                data-testid={`weekoff-cal-day-${c.iso}`}
                title={isHoliday ? 'Declared holiday — manage it in the Holiday calendar'
                  : isOff ? `Weekly off ${fromPolicy ? '(from policy)' : '(marked by HR)'} — click to make it a working day`
                    : isException ? 'Working day exception — click to make it a weekly off again'
                      : 'Click to mark this date as a paid weekly off'}
                className={`relative h-9 rounded-md border text-xs font-medium transition-all ${
                  isHoliday
                    ? 'bg-red-50 border-red-200 text-red-400 cursor-not-allowed'
                    : isOff
                      ? (fromPolicy
                        ? 'bg-blue-100 border-blue-300 text-blue-800'
                        : 'bg-blue-600 border-blue-600 text-white')
                      : isException
                        ? 'bg-amber-50 border-amber-300 text-amber-800 border-dashed'
                        : 'bg-white border-slate-200 text-slate-700 hover:bg-blue-50 hover:border-blue-300'
                }`}
              >
                {c.day}
              </button>
            );
          })}
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-slate-500">
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-blue-100 border border-blue-300" /> From policy</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-blue-600" /> Added by HR</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-amber-50 border border-dashed border-amber-300" /> Working day (policy off cancelled)</span>
        </div>
      </CardContent>
    </Card>
  );
}

export default WeeklyOffCalendar;
